import React from 'react';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { formatDateTimeDDMMYYYY } from '../../../utils/date';
import { MeinshkConnectModal, type MeinshkEditSettings } from '../components/MeinshkConnectModal';
import { MeinshkEmployeeMapTable, type MeinshkEmployeeMapRow } from '../components/MeinshkEmployeeMapTable';
import { MeinshkReconcilePanel } from '../components/MeinshkReconcilePanel';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsStatusChip } from '../components/SettingsStatusChip';
import { SettingsToggle } from '../components/SettingsToggle';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';

type StepStats = { ok: boolean; error?: string; skipped?: boolean; counts?: Record<string, number>; notes?: string[] };
type SyncStats = {
  startedAt: string;
  finishedAt?: string;
  trigger: 'schedule' | 'manual';
  steps: Record<string, StepStats>;
  unmatchedEmployees: Array<{ id: string; name: string }>;
  errors: string[];
};

type Settings = {
  clientId: string;
  hasClientPassword: boolean;
  complete: boolean;
  syncEnabled: boolean;
  pushMasterDataEnabled: boolean;
  pullTimesEnabled: boolean;
  syncPlanningEnabled: boolean;
  projectStageIds: string[];
  lastReconcileAt: string | null;
  nextRunAt: string | null;
  authFailedAt: string | null;
  lastStatusCode: number | null;
  lastStatusMessage: string | null;
  lastTestedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: 'ok' | 'error' | null;
  lastSyncError: string | null;
  lastSyncStats: SyncStats | null;
};

type Counts = {
  customers: number;
  projects: number;
  employeesMapped: number;
  employeesUnmapped: number;
  timesImported: number;
  timesUnmapped: number;
  rapportsImported: number;
  rapportsUnmapped: number;
};

type Pipeline = { id: string; name: string; stages: Array<{ id: string; label: string }> };

type Response = {
  canManage: boolean;
  settings: Settings | null;
  counts: Counts | null;
  employeeMap: MeinshkEmployeeMapRow[];
  pipelines: Pipeline[];
  syncRunning: boolean;
};

type UnmappedItem = {
  id: string;
  kind: 'time' | 'rapport';
  externalId: string;
  entryDate: string | null;
  meinshkEmployeeId: string | null;
  meinshkConstructionId: string | null;
  reason: string | null;
  lastError: string | null;
  description: string;
};

const STEP_LABEL: Record<string, string> = {
  employees: 'Mitarbeiter',
  customers: 'Kunden',
  projects: 'Projekte',
  schedules: 'Termine',
  tasks: 'Aufgaben',
  times: 'Zeiten',
  rapports: 'Rapporte',
  taskStatus: 'Aufgaben-Status',
};

const REASON_LABEL: Record<string, string> = {
  no_employee: 'Mitarbeiter nicht zugeordnet',
  no_construction: 'Projekt nicht verknüpft',
  day_stamp_conflict: 'Tag hat bereits einen eigenen Eintrag',
  absence_conflict: 'Tag hat bereits eine Abwesenheit',
  unknown_absence: 'Abwesenheitsart unbekannt',
  invalid: 'Ungültige Daten',
  error: 'Fehler beim Übernehmen, wird erneut versucht',
};

function stepSummary(name: string, s: StepStats): string {
  if (s.skipped) return 'aus';
  if (!s.ok) return s.error ? `Fehler: ${s.error}` : 'Fehler';
  const c = s.counts ?? {};
  const parts: string[] = [];
  const add = (key: string, label: string) => {
    if (c[key]) parts.push(`${c[key]} ${label}`);
  };
  if (name === 'employees') {
    add('autoLinked', 'neu zugeordnet');
    add('unmatched', 'offen');
  } else if (name === 'times') {
    add('mirrored', 'übernommen');
    add('deleted', 'gelöscht');
    for (const key of Object.keys(c)) if (key.startsWith('unmapped_')) parts.push(`${c[key]} ${REASON_LABEL[key.slice(9)] ?? key}`);
  } else if (name === 'rapports') {
    add('imported', 'neu');
    add('replaced', 'aktualisiert');
    add('unmapped_no_construction', 'ohne Projekt');
  } else if (name === 'taskStatus') {
    add('completed', 'erledigt');
    add('reopened', 'wieder offen');
  } else {
    add('inserted', 'angelegt');
    add('updated', 'aktualisiert');
    add('deactivated', 'deaktiviert');
    add('deleted', 'gelöscht');
    add('archived', 'archiviert');
    add('unchanged', 'unverändert');
    add('deferred', 'verschoben');
  }
  add('errors', 'Fehler');
  return parts.join(', ') || 'nichts zu tun';
}

export function MeinshkSettingsSection() {
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [headerNotice, setHeaderNotice] = React.useState<string | null>(null);
  const [data, setData] = React.useState<Response | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editSettings, setEditSettings] = React.useState<MeinshkEditSettings | null>(null);
  const [testing, setTesting] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [patching, setPatching] = React.useState(false);
  const [unmapped, setUnmapped] = React.useState<UnmappedItem[] | null>(null);
  const [showSteps, setShowSteps] = React.useState(false);

  const load = React.useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch('/api/settings/meinshk', { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'MeinSHK-Einstellungen konnten nicht geladen werden.');
      setData({
        canManage: Boolean(d.canManage),
        settings: d.settings ?? null,
        counts: d.counts ?? null,
        employeeMap: Array.isArray(d.employeeMap) ? d.employeeMap : [],
        pipelines: Array.isArray(d.pipelines) ? d.pipelines : [],
        syncRunning: Boolean(d.syncRunning),
      });
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const settings = data?.settings ?? null;
  const canManage = Boolean(data?.canManage);

  const loadUnmapped = React.useCallback(async () => {
    try {
      const res = await fetch('/api/settings/meinshk/unmapped?limit=50', { credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) setUnmapped(Array.isArray(d.items) ? d.items : []);
    } catch {
      setUnmapped([]);
    }
  }, []);

  React.useEffect(() => {
    if (settings && (data?.counts?.timesUnmapped || data?.counts?.rapportsUnmapped)) void loadUnmapped();
  }, [settings, data?.counts?.timesUnmapped, data?.counts?.rapportsUnmapped, loadUnmapped]);

  const openConnect = () => {
    setEditSettings(null);
    setModalOpen(true);
  };
  const openEdit = () => {
    if (!settings) return;
    setEditSettings({ clientId: settings.clientId, hasClientPassword: settings.hasClientPassword });
    setModalOpen(true);
  };

  const testConnection = async () => {
    setTesting(true);
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/meinshk/test', { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Prüfung fehlgeschlagen.');
      setHeaderNotice(`Verbindung geprüft, ${d.employees ?? 0} Mitarbeiter sichtbar.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
      await load({ silent: true });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/meinshk/sync', { method: 'POST', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Abgleich fehlgeschlagen.');
      const stats = d?.stats as SyncStats | undefined;
      setHeaderNotice(stats?.errors?.length ? `Abgleich mit ${stats.errors.length} Fehler${stats.errors.length === 1 ? '' : 'n'} beendet.` : 'Abgleich abgeschlossen.');
      setShowSteps(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
      await load({ silent: true });
      await loadUnmapped();
    }
  };

  const patch = async (body: Record<string, unknown>) => {
    setPatching(true);
    setActionError(null);
    try {
      const res = await fetch('/api/settings/meinshk', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Speichern fehlgeschlagen.');
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPatching(false);
    }
  };

  const toggleStage = (stageId: string) => {
    if (!settings) return;
    const next = settings.projectStageIds.includes(stageId)
      ? settings.projectStageIds.filter((id) => id !== stageId)
      : [...settings.projectStageIds, stageId];
    void patch({ projectStageIds: next });
  };

  const remove = async () => {
    if (!window.confirm('MeinSHK-Verbindung wirklich entfernen? Verknüpfungen und bereits übernommene Zeiten und Rapporte bleiben erhalten.')) return;
    setActionError(null);
    setHeaderNotice(null);
    try {
      const res = await fetch('/api/settings/meinshk', { method: 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Löschen fehlgeschlagen.');
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const bannerError = actionError && actionError !== settings?.lastStatusMessage ? actionError : null;
  const testedOk = settings?.lastStatusCode === 0;
  const testedFail = settings != null && settings.lastStatusCode != null && settings.lastStatusCode !== 0;
  const busy = testing || syncing || patching || Boolean(data?.syncRunning);
  const stats = settings?.lastSyncStats ?? null;
  const authPaused = Boolean(settings?.authFailedAt && Date.now() - Date.parse(settings.authFailedAt) < 15 * 60_000);

  return (
    <>
      <SettingsCard
        title="MeinSHK"
        description="Kunden, Projekte, Termine und Aufgaben an die MeinSHK-App übergeben; Arbeitszeiten, Abwesenheiten und Rapporte zurückholen."
        actions={
          <>
            <SettingsCardSavedStatus saved={Boolean(headerNotice)}>{headerNotice}</SettingsCardSavedStatus>
            {canManage && !loading && !settings ? (
              <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={openConnect}>
                <Plus className="w-4 h-4" strokeWidth={1.75} />
                Verbinden
              </button>
            ) : null}
          </>
        }
      >
        <div className="space-y-6">
          {loading ? (
            <InlineLoadingState label="Einstellungen werden geladen …" className="justify-start" />
          ) : (
            <>
              {loadError ? <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{loadError}</div> : null}
              {bannerError ? <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{bannerError}</div> : null}
              {!canManage && !settings ? <p className="text-sm text-app-text-subtle">Nur Inhaber oder Administratoren können MeinSHK verbinden.</p> : null}
              {!settings && canManage ? (
                <p className="text-sm text-app-text-subtle">
                  Die Zugangsdaten (Client-ID und API-Passwort) finden sich im MeinSHK-Verwaltungssystem. Nach dem Verbinden zuerst Mitarbeiter zuordnen und
                  den Bestandsabgleich ausführen, dann die Übertragung einschalten.
                </p>
              ) : null}

              {settings ? (
                <>
                  <div className="rounded-md border border-app-border px-3 py-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <div className="min-w-0 sm:flex-1">
                        <p className="text-sm font-medium text-app-text leading-5 truncate">Client-ID {settings.clientId || '—'}</p>
                        <p className="text-xs text-app-text-subtle mt-0.5 truncate">
                          {settings.lastSyncAt ? `Letzter Abgleich ${formatDateTimeDDMMYYYY(settings.lastSyncAt)}` : 'Noch kein Abgleich'}
                          {settings.syncEnabled && settings.nextRunAt ? ` · nächster ${formatDateTimeDDMMYYYY(settings.nextRunAt)}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                          {testedOk ? <SettingsStatusChip tone="ok">Geprüft</SettingsStatusChip> : null}
                          {testedFail ? <SettingsStatusChip tone="err">Prüfung fehlgeschlagen</SettingsStatusChip> : null}
                          {settings.lastStatusCode == null ? <SettingsStatusChip tone="muted">Noch nicht geprüft</SettingsStatusChip> : null}
                          {authPaused ? <SettingsStatusChip tone="warn">Pausiert nach Login-Fehler</SettingsStatusChip> : null}
                          {settings.lastSyncStatus === 'error' ? <SettingsStatusChip tone="err">Abgleich mit Fehlern</SettingsStatusChip> : null}
                          {data?.syncRunning ? <SettingsStatusChip tone="muted">Abgleich läuft …</SettingsStatusChip> : null}
                          {!settings.syncEnabled ? <SettingsStatusChip tone="muted">Automatik aus</SettingsStatusChip> : null}
                        </div>
                        {canManage ? (
                          <div className="flex items-center gap-2 shrink-0">
                            <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={() => void testConnection()} disabled={busy}>
                              {testing ? <LoadingSpinner size="sm" /> : null}
                              Prüfen
                            </button>
                            <button
                              type="button"
                              className={SETTINGS_BTN_OUTLINE}
                              onClick={() => void syncNow()}
                              disabled={busy || !settings.complete}
                              title="Abgleich jetzt ausführen"
                            >
                              {syncing || data?.syncRunning ? <LoadingSpinner size="sm" /> : <RefreshCw className="w-3.5 h-3.5" />}
                              Jetzt synchronisieren
                            </button>
                            <button type="button" className={`${SETTINGS_BTN_OUTLINE} !px-2`} onClick={openEdit} title="Zugang bearbeiten">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" className={`${SETTINGS_BTN_DANGER_OUTLINE} !px-2`} onClick={() => void remove()} title="Verbindung entfernen">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {testedFail && settings.lastStatusMessage ? <p className="text-xs text-red-700">{settings.lastStatusMessage}</p> : null}
                  {settings.lastSyncStatus === 'error' && settings.lastSyncError ? <p className="text-xs text-red-700">{settings.lastSyncError}</p> : null}

                  {data?.counts ? (
                    <div className="grid gap-3 sm:grid-cols-4">
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Mitarbeiter</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.employeesMapped}</p>
                        <p className="text-xs text-app-text-subtle">{data.counts.employeesUnmapped} nicht zugeordnet</p>
                      </div>
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Verknüpft</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.projects}</p>
                        <p className="text-xs text-app-text-subtle">Projekte · {data.counts.customers} Kunden</p>
                      </div>
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Zeiten</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.timesImported}</p>
                        <p className="text-xs text-app-text-subtle">{data.counts.timesUnmapped} nicht zuordenbar</p>
                      </div>
                      <div className="rounded-md border border-app-border p-3">
                        <p className="text-xs font-medium text-app-text-subtle">Rapporte</p>
                        <p className="mt-1 text-lg font-semibold text-app-text">{data.counts.rapportsImported}</p>
                        <p className="text-xs text-app-text-subtle">{data.counts.rapportsUnmapped} ohne Projekt</p>
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-3">
                    <h3 className={SETTINGS_FIELD_LABEL}>Was läuft</h3>
                    <div className="divide-y divide-app-border rounded-md border border-app-border">
                      <ToggleRow
                        label="Automatisch abgleichen"
                        hint="Alle 15 Minuten im Hintergrund. Aus = nur über „Jetzt synchronisieren“."
                        active={settings.syncEnabled}
                        disabled={!canManage || patching}
                        onClick={() => void patch({ syncEnabled: !settings.syncEnabled })}
                      />
                      <ToggleRow
                        label="Stammdaten nach MeinSHK übertragen"
                        hint={
                          settings.lastReconcileAt
                            ? 'Kunden, Ansprechpartner und Projekte der gewählten Phasen anlegen und aktuell halten.'
                            : 'Erst nach dem Bestandsabgleich möglich, damit keine Dubletten entstehen.'
                        }
                        active={settings.pushMasterDataEnabled}
                        disabled={!canManage || patching || (!settings.pushMasterDataEnabled && !settings.lastReconcileAt)}
                        onClick={() => void patch({ pushMasterDataEnabled: !settings.pushMasterDataEnabled })}
                      />
                      <ToggleRow
                        label="Zeiten und Rapporte holen"
                        hint="Arbeitszeiten und Abwesenheiten zugeordneter Mitarbeiter in die Zeiterfassung, Rapport-PDFs und Fotos in die Projektdokumente. Importierte Zeiten sind hier schreibgeschützt."
                        active={settings.pullTimesEnabled}
                        disabled={!canManage || patching}
                        onClick={() => void patch({ pullTimesEnabled: !settings.pullTimesEnabled })}
                      />
                      <ToggleRow
                        label="Termine und Aufgaben"
                        hint="Termine mit Projekt und zugeordneten Mitarbeitern sowie Aufgaben nach MeinSHK übertragen; Erledigungen aus MeinSHK zurückholen. Nur einschalten, wenn hier geplant wird."
                        active={settings.syncPlanningEnabled}
                        disabled={!canManage || patching}
                        onClick={() => void patch({ syncPlanningEnabled: !settings.syncPlanningEnabled })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className={SETTINGS_FIELD_LABEL}>Projekte ab diesen Phasen übertragen</h3>
                    <p className="text-xs text-app-text-subtle">
                      Nur Projekte in den markierten Pipeline-Phasen werden nach MeinSHK gegeben. Verlässt ein Projekt die Phasen oder geht verloren, wird es in
                      MeinSHK inaktiv.
                    </p>
                    {data?.pipelines.length ? (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {data.pipelines.map((p) => (
                          <div key={p.id} className="rounded-md border border-app-border p-3">
                            <p className="text-xs font-medium text-app-text mb-2">{p.name}</p>
                            <div className="space-y-1.5">
                              {p.stages.map((st) => (
                                <label key={st.id} className="flex items-center gap-2 text-sm text-app-text">
                                  <input
                                    type="checkbox"
                                    checked={settings.projectStageIds.includes(st.id)}
                                    disabled={!canManage || patching}
                                    onChange={() => toggleStage(st.id)}
                                  />
                                  {st.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-app-text-subtle">Keine Pipelines gefunden.</p>
                    )}
                  </div>

                  <MeinshkReconcilePanel canManage={canManage} lastReconcileAt={settings.lastReconcileAt} onDone={() => void load({ silent: true })} />

                  <MeinshkEmployeeMapTable
                    rows={data?.employeeMap ?? []}
                    canManage={canManage}
                    onChanged={(rows) => setData((prev) => (prev ? { ...prev, employeeMap: rows } : prev))}
                  />

                  {stats ? (
                    <div className="space-y-2">
                      <button type="button" className="text-xs font-medium text-pipedrive-blue hover:underline" onClick={() => setShowSteps((v) => !v)}>
                        {showSteps ? 'Letzten Abgleich ausblenden' : `Letzten Abgleich anzeigen (${formatDateTimeDDMMYYYY(stats.startedAt)})`}
                      </button>
                      {showSteps ? (
                        <div className="overflow-hidden rounded-md border border-app-border">
                          <table className="w-full text-xs">
                            <thead className="bg-app-surface-muted text-left">
                              <tr>
                                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Schritt</th>
                                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Ergebnis</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(stats.steps).map(([name, s]) => (
                                <tr key={name} className="border-t border-app-border align-top">
                                  <td className="px-3 py-2 font-medium text-app-text whitespace-nowrap">{STEP_LABEL[name] ?? name}</td>
                                  <td className={`px-3 py-2 ${s.ok ? 'text-app-text-muted' : 'text-red-700'}`}>
                                    {stepSummary(name, s)}
                                    {s.notes?.length ? (
                                      <ul className="mt-1 list-disc pl-4 text-app-text-subtle">
                                        {s.notes.slice(0, 5).map((n, i) => (
                                          <li key={i}>{n}</li>
                                        ))}
                                      </ul>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {unmapped?.length ? (
                    <div className="space-y-2">
                      <h3 className={SETTINGS_FIELD_LABEL}>Nicht zuordenbar</h3>
                      <p className="text-xs text-app-text-subtle">
                        Diese Einträge aus MeinSHK konnten noch nicht übernommen werden. Nach einer Mitarbeiter-Zuordnung oder Projekt-Verknüpfung werden sie beim
                        nächsten Abgleich erneut versucht.
                      </p>
                      <div className="overflow-hidden rounded-md border border-app-border">
                        <table className="w-full text-xs">
                          <thead className="bg-app-surface-muted text-left">
                            <tr>
                              <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Art</th>
                              <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Datum</th>
                              <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>MeinSHK-IDs</th>
                              <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Grund</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unmapped.map((item) => (
                              <tr key={item.id} className="border-t border-app-border">
                                <td className="px-3 py-2 text-app-text">{item.kind === 'time' ? 'Zeit' : 'Rapport'}</td>
                                <td className="px-3 py-2 text-app-text-muted whitespace-nowrap">
                                  {item.entryDate ? `${item.entryDate.slice(8, 10)}.${item.entryDate.slice(5, 7)}.${item.entryDate.slice(0, 4)}` : '—'}
                                </td>
                                <td className="px-3 py-2 font-mono text-app-text-subtle">
                                  {item.externalId}
                                  {item.meinshkEmployeeId ? ` · MA ${item.meinshkEmployeeId}` : ''}
                                  {item.meinshkConstructionId ? ` · Projekt ${item.meinshkConstructionId}` : ''}
                                </td>
                                <td className="px-3 py-2">
                                  <SettingsStatusChip tone={item.lastError ? 'err' : 'warn'}>
                                    {item.lastError ? 'Fehler' : REASON_LABEL[item.reason ?? ''] ?? item.reason ?? '—'}
                                  </SettingsStatusChip>
                                  {item.lastError ? <div className="mt-1 text-red-700">{item.lastError}</div> : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>
      </SettingsCard>

      <MeinshkConnectModal
        open={modalOpen}
        editSettings={editSettings}
        onClose={() => {
          setModalOpen(false);
          setEditSettings(null);
        }}
        onSaved={async (result) => {
          setModalOpen(false);
          setEditSettings(null);
          await load({ silent: true });
          if (result.testedOk) {
            setActionError(null);
            setHeaderNotice('Gespeichert und verbunden.');
          } else {
            setHeaderNotice(null);
            setActionError(result.testError || 'Gespeichert, Prüfung fehlgeschlagen.');
          }
        }}
      />
    </>
  );
}

function ToggleRow({
  label,
  hint,
  active,
  disabled,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-app-text">{label}</p>
        <p className="text-xs text-app-text-subtle mt-0.5">{hint}</p>
      </div>
      <SettingsToggle active={active} disabled={disabled} onClick={onClick} label={label} />
    </div>
  );
}
